import { portalAPI, portalURL } from '@/helpers/environmentHelper';
import { logToServer } from '@/services/LogService';
import useAbortController from '@/utils/hooks/useAbortController';
import { useCurrency } from '@/utils/hooks/useCurrency';
import useCurrencyOptions from '@/utils/hooks/useCurrencyOptions';
import { useCurrencySymbol } from '@/utils/hooks/useCurrencySymbol';
import useLanguage from '@/utils/hooks/useLanguage';
import { use401Error } from '@/utils/hooks/usePortalAuthentication';
import useToast from '@/utils/hooks/useToast';
import { fetchPortalAPI, usePortalSWR, useSso } from '@/utils/providers/SSOProvider';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDebouncedValue } from '@mantine/hooks';
import axios from 'axios';
import BigNumber from 'bignumber.js';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNumberFormatter } from 'react-aria';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Button } from '../common/Button';
import Divider from '../common/Divider';
import PortalEntry from '../common/PortalEntry';
import { getDefaultCurrencyId } from '../form/schema/common';
import {
    PortalInternalTransferFormInput,
    PortalInternalTransferFormValues,
    portalInternalTransferSchema
} from '../form/schema/portalInternalTransferSchema';
import ErrorMessage from '../inputs/ErrorMessage';
import FilterSelect from '../inputs/FilterSelect';
import PortalInputController from '../inputs/PortalInputController';
import PortalNumberInput from '../inputs/PortalNumberInput';
import Select from '../inputs/Select';
import TextArea from '../inputs/TextArea';
import Modal, { ModalClose, ModalHeader, ModalProps, ModalTitle } from './Modal';
import { EntityAttributes } from '@/constants/general';
import Tooltip from '../common/Tooltip';
import { MdInfo } from 'react-icons/md';
import CurrencyIcon from '../common/CurrencyIcon';
import { FaExchangeAlt } from 'react-icons/fa';
import { StableCoinsResponse } from '@/types/sso';

type Option = {
    label: string;
    value: string;
};

export enum TransferType {
    FIAT = 'FIAT',
    CRYPTO = 'CRYPTO'
}

export const transferTypeOptions: Option[] = [
    {
        label: TransferType.FIAT,
        value: TransferType.FIAT
    },
    {
        label: TransferType.CRYPTO,
        value: TransferType.CRYPTO
    }
];

const supportedContraCurrencies = ['USDT', 'USD', 'USDC', 'EURC', 'EUR'];

const defaultCounterPartId = { label: 'Select a transfer counterpart', value: undefined };
const defaultCurrencyId = getDefaultCurrencyId('transfer', 'currency');
const defaultAssetId = getDefaultCurrencyId('transfer', 'asset');
const defaultInternalTransferType = transferTypeOptions[0];
const defaultContraCurrencyId = { label: 'Please select a contra currency', value: undefined };

const defaultValues: Partial<PortalInternalTransferFormInput> = {
    amount: undefined,
    transferType: defaultInternalTransferType,
    counterPartId: defaultCounterPartId,
    currencyId: defaultCurrencyId,
    contraCurrencyId: defaultContraCurrencyId,
    requestDescription: ''
};

interface PortalInternalTransferModalProps extends ModalProps {
    onRefresh?: () => void;
}

const PortalInternalTransferModal = (props: PortalInternalTransferModalProps) => {
    const { opened, handlers, onRefresh } = props;

    const lang = useLanguage();
    const {
        jwt,
        foundEntity,
        balancesApi,
        linkedEntitiesApi,
        flattenEntities,
        entityType,
        currenciesApi,
        instrumentCurrenciesApi,
        onFetchJWT
    } = useSso();

    const { data: singleCurrencyInstruments } = instrumentCurrenciesApi;
    const { on401Error } = use401Error();

    const { data: cashEntryCurrencies } = currenciesApi;

    const [error, setError] = useState<any>({ message: '' });
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [toastSuccess] = useToast();

    const isMarginEntity = useMemo(() => {
        return Boolean(entityType?.isMarginClient || foundEntity?.attributes?.includes(EntityAttributes.margin));
    }, [entityType?.isMarginClient, foundEntity?.attributes]);

    const filteredTransferTypeOptions = useMemo(() => {
        if (isMarginEntity) {
            return transferTypeOptions.filter((option) => option.value === TransferType.FIAT);
        }
        return transferTypeOptions;
    }, [isMarginEntity]);

    const isTransferTypeLocked = filteredTransferTypeOptions.length === 1;

    const formInstance = useForm<PortalInternalTransferFormInput>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(portalInternalTransferSchema())
    });

    const {
        reset,
        watch,
        setValue,
        getValues,
        setError: setFormError,
        handleSubmit,
        formState: { isValid }
    } = formInstance;

    const [amount, selectedTransferType, selectedCounterPartId, selectedCurrency, selectedContraCurrency] = watch([
        'amount',
        'transferType',
        'counterPartId',
        'currencyId',
        'contraCurrencyId'
    ]);

    const effectiveTransferType = useMemo(() => {
        return (
            filteredTransferTypeOptions.find((option) => option.value === selectedTransferType?.value) ||
            filteredTransferTypeOptions[0]
        );
    }, [filteredTransferTypeOptions, selectedTransferType?.value]);

    const { data: balances } = balancesApi;
    const { data: linkedEntities } = linkedEntitiesApi;

    const { transferCcyOptions } = useCurrencyOptions(effectiveTransferType);
    const { abortController, setAbortController, onClose } = useAbortController({
        isLoading,
        handlers
    });

    const counterPartOptions = useMemo(() => {
        if (!linkedEntities || !foundEntity) {
            return [];
        }

        return linkedEntities
            .filter((entity) => entity.entityId !== foundEntity?.entityId)
            .map((entity) => ({
                label: entity.entityName,
                value: entity.entityId
            }));
    }, [linkedEntities, foundEntity]);

    const selectedCounterPartEntityId = useMemo(() => {
        const value = Number(selectedCounterPartId?.value);
        return Number.isFinite(value) && value > 0 ? value : undefined;
    }, [selectedCounterPartId]);

    const counterPartEntityDetails = useMemo(() => {
        if (!selectedCounterPartEntityId || !flattenEntities?.length) {
            return undefined;
        }

        return flattenEntities.find((entity) => Number(entity.entityId) === selectedCounterPartEntityId);
    }, [selectedCounterPartEntityId, flattenEntities]);

    const isCounterPartAMargin = useMemo(() => {
        return Boolean(counterPartEntityDetails?.attributes?.includes(EntityAttributes.margin));
    }, [counterPartEntityDetails]);

    const isCurrentEntityAMargin = useMemo(() => {
        return Boolean(entityType?.isMarginClient || foundEntity?.attributes?.includes(EntityAttributes.margin));
    }, [entityType?.isMarginClient, foundEntity?.attributes]);

    const isCurrentEntityDeliverable = useMemo(() => {
        return Boolean(
            entityType?.isDeliverableClient || foundEntity?.attributes?.includes(EntityAttributes.deliverable)
        );
    }, [entityType?.isDeliverableClient, foundEntity?.attributes]);

    const isCounterPartEntityDeliverable = useMemo(() => {
        return Boolean(counterPartEntityDetails?.attributes?.includes(EntityAttributes.deliverable));
    }, [counterPartEntityDetails]);

    // Strictly check if transfer is Deliverable -> Margin OR Margin -> Deliverable
    const isInternalTransferBetweenDeliverableToMarginEntity = useMemo(() => {
        if (!counterPartEntityDetails) {
            return false;
        }

        return (
            (isCurrentEntityAMargin && isCounterPartEntityDeliverable) ||
            (isCurrentEntityDeliverable && isCounterPartAMargin)
        );
    }, [
        counterPartEntityDetails,
        isCurrentEntityAMargin,
        isCurrentEntityDeliverable,
        isCounterPartAMargin,
        isCounterPartEntityDeliverable
    ]);

    const showContraCurrencyField = useMemo(() => {
        return supportedContraCurrencies.includes(selectedCurrency?.label || '');
    }, [selectedCurrency?.label]);

    // Only show contra currency conversion if it's Deliverable <-> Margin AND the currency supports contra
    const shouldShowContraCurrency = useMemo(() => {
        return isInternalTransferBetweenDeliverableToMarginEntity && showContraCurrencyField;
    }, [isInternalTransferBetweenDeliverableToMarginEntity, showContraCurrencyField]);

    const currencyConfig = useCurrency(selectedCurrency?.name || '');

    const balanceFormatter = useNumberFormatter({
        minimumFractionDigits: currencyConfig?.order_decimals > 1 ? currencyConfig?.order_decimals - 2 : 0,
        maximumFractionDigits: currencyConfig?.order_decimals || 0
    });

    const currentBalance = useMemo(() => {
        return balances?.find((balance: any) => balance.currencyName === selectedCurrency?.label)?.balance || 0;
    }, [balances, selectedCurrency]);

    const [debouncedAmount] = useDebouncedValue(amount, 150);
    const [debouncedBalance] = useDebouncedValue(currentBalance, 150);

    const prevTransferTypeRef = useRef<string | undefined>(undefined);

    // Initial reset on Modal Open/Close ONLY
    useEffect(() => {
        if (opened) {
            balancesApi.mutate();
            linkedEntitiesApi.mutate();
            instrumentCurrenciesApi.mutate();
            const controller = new AbortController();
            setAbortController(controller);

            const initialTransferType = effectiveTransferType || defaultInternalTransferType;
            const initialCurrency =
                initialTransferType.value === TransferType.FIAT ? defaultCurrencyId : defaultAssetId;
            const initialCounterpart = counterPartOptions?.length === 1 ? counterPartOptions[0] : defaultCounterPartId;

            reset({
                ...defaultValues,
                transferType: initialTransferType,
                currencyId: initialCurrency,
                counterPartId: initialCounterpart
            });
            setError({ message: '' });
            prevTransferTypeRef.current = initialTransferType.value;
        } else {
            reset(defaultValues, { keepIsValid: false });
            setError({ message: '' });
            prevTransferTypeRef.current = undefined;
            abortController?.abort();
        }

        return () => abortController?.abort();
    }, [opened]);

    // Set counterpart if options load late
    useEffect(() => {
        if (opened && counterPartOptions?.length === 1) {
            const currentCounterPart = getValues('counterPartId');
            if (!currentCounterPart?.value) {
                setValue('counterPartId', counterPartOptions[0]);
            }
        }
    }, [opened, counterPartOptions, setValue, getValues]);

    // Transfer type switch handling
    useEffect(() => {
        if (!opened || !effectiveTransferType?.value) return;

        const currentType = effectiveTransferType.value;

        if (prevTransferTypeRef.current && prevTransferTypeRef.current !== currentType) {
            setValue('transferType', effectiveTransferType);
            setValue('currencyId', currentType === TransferType.FIAT ? defaultCurrencyId : defaultAssetId);
            setValue('amount', '');
            setError({ message: '' });
        }

        prevTransferTypeRef.current = currentType;
    }, [opened, effectiveTransferType, setValue]);

    // Check balance on currency change
    useEffect(() => {
        if (opened && selectedCurrency?.value) {
            if (currentBalance <= 0) {
                setError({ message: 'Insufficient balance for withdrawal' });
            } else {
                setError({ message: '' });
            }
        }
    }, [opened, selectedCurrency?.value, currentBalance]);

    // Amount vs Available Balance error trigger
    useEffect(() => {
        if (debouncedAmount && debouncedBalance) {
            if (BigNumber(debouncedAmount).isGreaterThan(debouncedBalance)) {
                setFormError('amount', {
                    type: 'manual',
                    message: 'Amount exceeded available balance.'
                });
            }
        }
    }, [debouncedAmount, debouncedBalance, setFormError]);

    const availableBalance = useMemo(() => {
        if (currentBalance) {
            return (
                <div
                    className={cn('flex items-center gap-1', {
                        'text-brand-green-light': currentBalance > 0,
                        'text-brand-red-light': currentBalance < 0
                    })}>
                    {balanceFormatter.format(currentBalance)}
                </div>
            );
        }

        return selectedCurrency?.name ? 'No available balance' : 'Select a transfer currency';
    }, [currentBalance, balanceFormatter, selectedCurrency]);

    const onSubmit = async (data: PortalInternalTransferFormInput) => {
        setIsLoading(true);
        const dataValidated = data as PortalInternalTransferFormValues;

        const includeContraCurrencyId =
            shouldShowContraCurrency && data.contraCurrencyId?.value !== data.currencyId?.value;

        try {
            if (foundEntity) {
                const payload = {
                    entityId: foundEntity.entityId,
                    amount: dataValidated.amount || 0,
                    requestDescription: dataValidated.requestDescription || null,
                    currencyId: dataValidated.currencyId.value,
                    counterpartEntityId: dataValidated.counterPartId.value,
                    contraCurrencyId: includeContraCurrencyId ? data.contraCurrencyId?.value || null : null
                };
                if (portalURL) {
                    await axios.post(portalAPI('/api/requests/internal-transfer'), payload, {
                        headers: { Authorization: `Bearer ${jwt}` },
                        signal: abortController.signal
                    });
                }
                const title = 'Internal Transfer';
                const body = 'Internal Transfer request submitted successfully';
                toastSuccess({ body, title });
                onRefresh?.();
            }
            handlers.close();
        } catch (err: any) {
            on401Error(error, () => {
                logToServer(
                    'error',
                    'Unauthenticated access on /api/requests/internal-transfer. JWT may be invalid or expired.'
                );
            });
            setError(err?.response || err);
        }
        setIsLoading(false);
    };

    const onError = (e: FieldErrors) => console.log(e);

    const stableCoinApi = usePortalSWR<StableCoinsResponse[]>('/api/common/stable-coins', () =>
        fetchPortalAPI(onFetchJWT, '/api/common/stable-coins', jwt)
    );

    useEffect(() => {
        if (opened && shouldShowContraCurrency) {
            stableCoinApi.mutate();
        }
    }, [opened, shouldShowContraCurrency]);

    const { data: stableCoinsData, isLoading: isStableCoinsLoading } = stableCoinApi;

    const isFiatTransferType = useMemo(() => {
        return effectiveTransferType?.value === TransferType.FIAT;
    }, [effectiveTransferType?.value]);

    const formatContraCurrencyOptions = useCallback((cashEntryCurrencies: any[], listOfCurrenciesIDs: number[]) => {
        if (!cashEntryCurrencies) {
            return [];
        }
        return cashEntryCurrencies
            .filter((currency) => listOfCurrenciesIDs.includes(currency.id))
            .map((currency) => ({
                label: currency.digitalAssetName || currency.name,
                value: currency.id
            }));
    }, []);

    // COMPUTED CONTRA CURRENCY OPTIONS
    const contraCurrencyOptions = useMemo(() => {
        if (!selectedCurrency?.value) {
            return [];
        }

        // IF NOT Deliverable <-> Margin transfer, contra currency is STRICTLY the same currency
        if (!isInternalTransferBetweenDeliverableToMarginEntity) {
            return formatContraCurrencyOptions(cashEntryCurrencies || [], [selectedCurrency.value]);
        }

        const isSelectedCurrencySupported = supportedContraCurrencies.includes(selectedCurrency?.label || '');

        const relatedCurrencyIds = isSelectedCurrencySupported
            ? (stableCoinsData || [])
                  .filter((stableCoin) => {
                      return isFiatTransferType
                          ? stableCoin.fiatCurrencyId === selectedCurrency.value
                          : stableCoin.cryptoCurrencyId === selectedCurrency.value;
                  })
                  .map((item) => (isFiatTransferType ? item.cryptoCurrencyId : item.fiatCurrencyId))
            : [];

        // RULE: Margin as receiver can ONLY receive FIAT
        if (isCounterPartAMargin) {
            if (isFiatTransferType) {
                // If main currency is FIAT, Margin receives same FIAT
                return formatContraCurrencyOptions(cashEntryCurrencies || [], [selectedCurrency.value]);
            } else {
                // If main currency is CRYPTO (e.g., USDT), Margin can ONLY receive the related FIAT (e.g., USD)
                return formatContraCurrencyOptions(cashEntryCurrencies || [], relatedCurrencyIds);
            }
        }

        // RULE: Margin -> Deliverable (Margin sender sends FIAT, Deliverable can receive FIAT or Crypto)
        if (isCurrentEntityAMargin && isCounterPartEntityDeliverable) {
            if (isSelectedCurrencySupported && relatedCurrencyIds.length > 0) {
                return formatContraCurrencyOptions(cashEntryCurrencies || [], [
                    selectedCurrency.value,
                    ...relatedCurrencyIds
                ]);
            }
            return formatContraCurrencyOptions(cashEntryCurrencies || [], [selectedCurrency.value]);
        }

        return formatContraCurrencyOptions(cashEntryCurrencies || [], [selectedCurrency.value]);
    }, [
        selectedCurrency,
        isInternalTransferBetweenDeliverableToMarginEntity,
        supportedContraCurrencies,
        stableCoinsData,
        isFiatTransferType,
        isCounterPartAMargin,
        isCurrentEntityAMargin,
        isCounterPartEntityDeliverable,
        cashEntryCurrencies,
        formatContraCurrencyOptions
    ]);

    // Handle updates to selected contra currency option
    useEffect(() => {
        if (!opened) return;

        if (!shouldShowContraCurrency) {
            if (selectedCurrency?.value) {
                setValue('contraCurrencyId', {
                    label: selectedCurrency?.label || selectedCurrency?.name || '',
                    value: selectedCurrency?.value
                });
            }
            return;
        }

        const currentContra = getValues('contraCurrencyId');
        const isCurrentValid = contraCurrencyOptions.some((opt) => opt.value === currentContra?.value);

        if (!isCurrentValid) {
            if (contraCurrencyOptions.length >= 1) {
                // Auto-select the valid option (e.g., USD when sending USDT to Margin)
                setValue('contraCurrencyId', contraCurrencyOptions[0]);
            } else {
                setValue('contraCurrencyId', defaultContraCurrencyId);
            }
        }
    }, [opened, contraCurrencyOptions, shouldShowContraCurrency, selectedCurrency?.value, setValue, getValues]);

    const selectedInstrument = useMemo(() => {
        if (!singleCurrencyInstruments || !selectedCurrency?.value || !selectedContraCurrency?.value) {
            return undefined;
        }
        return singleCurrencyInstruments.find((instrument) => {
            return (
                instrument.assetName === selectedContraCurrency.label ||
                instrument.code === selectedContraCurrency.label
            );
        });
    }, [singleCurrencyInstruments, selectedCurrency?.value, selectedContraCurrency]);

    const formattedContra = useMemo(
        () =>
            selectedInstrument && amount
                ? BigNumber(amount)
                      .abs()
                      .decimalPlaces(selectedInstrument?.decimals || 2)
                      .toFormat()
                : '',
        [selectedInstrument, amount]
    );

    const showContraAmountInfo = useMemo(() => {
        return Boolean(amount && selectedContraCurrency?.value && selectedInstrument && selectedCounterPartId?.value);
    }, [amount, selectedContraCurrency?.value, selectedInstrument, selectedCounterPartId?.value]);

    return (
        <Modal {...props} className="h-auto" size="max-w-lg">
            <ModalHeader>
                <ModalTitle>{lang.portal.makeAnInternalTransfer}</ModalTitle>
                <ModalClose handlers={handlers} />
            </ModalHeader>
            <Divider neutral />
            <FormProvider {...formInstance}>
                <form onSubmit={handleSubmit(onSubmit, onError)} className="relative h-full w-full text-brand-text">
                    <div className="flex flex-col gap-4 py-4 px-2 sm:px-4">
                        <div className="flex flex-col gap-4 text-sm">
                            <fieldset className="space-y-3">
                                <PortalEntry
                                    label={lang.portal.availableBalance}
                                    value={availableBalance}
                                    className={cn('', {
                                        ['text-brand-text text-sm']:
                                            !selectedCurrency?.name || availableBalance === 'No available balance'
                                    })}
                                    valueOnly
                                />
                                <div className="flex flex-col xsm:flex-row text-sm gap-4 w-full">
                                    <div className="flex-1">
                                        <PortalInputController
                                            name="transferType"
                                            label={lang.portal.transferType}
                                            placeholder={lang.portal.transferType}
                                            disabled={isTransferTypeLocked}
                                            required>
                                            <Select options={filteredTransferTypeOptions} />
                                        </PortalInputController>
                                    </div>
                                    <div className="flex-1">
                                        <PortalInputController
                                            name="currencyId"
                                            label={
                                                selectedTransferType?.value === 'FIAT'
                                                    ? lang.lite.currency
                                                    : lang.lite.asset
                                            }
                                            placeholder={
                                                selectedTransferType?.value === 'FIAT'
                                                    ? lang.lite.currency
                                                    : lang.lite.asset
                                            }
                                            required>
                                            <FilterSelect
                                                options={transferCcyOptions}
                                                enableCurrencyIcon
                                                enableSorting
                                            />
                                        </PortalInputController>
                                    </div>
                                </div>
                            </fieldset>
                            <div className="flex-1">
                                <PortalInputController
                                    name="amount"
                                    label={lang.portal.amountToTransfer}
                                    placeholder="0.00"
                                    required
                                    disabled={!selectedCurrency?.value}>
                                    <PortalNumberInput currency={selectedCurrency?.name} />
                                </PortalInputController>
                            </div>
                            <fieldset className="space-y-3">
                                <div className="flex flex-col xsm:flex-row text-sm gap-4 w-full">
                                    <div className="flex-1">
                                        <PortalEntry
                                            disabled
                                            label={lang.portal.fromAccount}
                                            value={foundEntity?.entityName}
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <PortalInputController
                                            name="counterPartId"
                                            label={lang.portal.toAccount}
                                            placeholder={lang.portal.toAccount}
                                            disabled={counterPartOptions?.length < 2}
                                            required>
                                            <Select options={counterPartOptions} enableSorting />
                                        </PortalInputController>
                                    </div>
                                </div>
                            </fieldset>

                            <fieldset className="space-y-3">
                                <PortalInputController
                                    name="contraCurrencyId"
                                    asyncInput={isStableCoinsLoading}
                                    disabled={contraCurrencyOptions?.length <= 1}
                                    hasTooltipIcon
                                    toolTip={
                                        <Tooltip placement="top" content={lang.portal.contraCurrencyTooltip}>
                                            <MdInfo />
                                        </Tooltip>
                                    }
                                    label={lang.portal.contraCurrency}
                                    placeholder={lang.portal.contraCurrency}
                                    required={shouldShowContraCurrency}>
                                    <FilterSelect options={contraCurrencyOptions} enableCurrencyIcon enableSorting />
                                </PortalInputController>

                                {showContraAmountInfo && (
                                    <div
                                        className={cn(
                                            'flex items-center justify-between px-3 py-1.5 rounded-md border gap-4 [background:color-mix(in_srgb,var(--color-brand-green)_20%,transparent)]  [border-color:color-mix(in_srgb,var(--color-brand-green)_30%,transparent)]'
                                        )}>
                                        <div className="flex items-center gap-2 text-sm text-brand-neutral-200 ">
                                            <FaExchangeAlt className="text-xs opacity-60" />
                                            <span>{lang.blotters.contraAmount}</span>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <div className="flex items-center gap-2 text-base font-semibold text-brand-neutral-200">
                                                <span>{lang.portal.transfer}</span>
                                                <CurrencyIcon ccy={selectedContraCurrency?.label || ''} />
                                                <span>{formattedContra}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </fieldset>

                            <PortalInputController
                                name="requestDescription"
                                label={lang.portal.requestDescription}
                                placeholder={lang.portal.requestDescription}>
                                <TextArea rows={2} />
                            </PortalInputController>

                            <ErrorMessage error={error} />
                        </div>
                    </div>
                    <Divider neutral />
                    <div className="flex space-x-2 justify-end text-brand-text text-sm items-center p-2 sm:p-4">
                        <button
                            type="button"
                            className="w-auto rounded-md p-2 px-4 bg-brand-neutral-700 hover:bg-brand-neutral-600"
                            onClick={() => onClose()}>
                            {lang.common.cancel}
                        </button>
                        <Button
                            type="button"
                            className="w-auto rounded-md p-2 px-4 bg-brand-primary hover:bg-brand-primary-light"
                            onClick={() => {
                                if (!isLoading) handleSubmit(onSubmit, onError)();
                            }}
                            disabled={!isValid || currentBalance <= 0}
                            isLoading={isLoading}>
                            {lang.portal.submitRequest}
                        </Button>
                    </div>
                </form>
            </FormProvider>
        </Modal>
    );
};

export default PortalInternalTransferModal;

interface WhitelistErrorProps {
    type: 'FIAT' | 'CRYPTO';
}

export const WhitelistError = (props: WhitelistErrorProps) => {
    const navigate = useNavigate();
    const lang = useLanguage();
    const { type } = props;

    return (
        <div className="flex flex-col gap-3">
            <span>
                {type === 'FIAT' ? lang.portal.whitelistBankAccountError : lang.portal.whitelistWalletAddressError}
                <button
                    onClick={() => {
                        if (type === 'FIAT') {
                            navigate('/portal/bank-accounts');
                        } else {
                            navigate('/portal/wallets');
                        }
                    }}
                    className="italic text-brand-text hover:underline mx-1">
                    {lang.common.here}
                </button>
                .
            </span>
        </div>
    );
};
